import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WorkspacePage from '../components/layout/WorkspacePage';
import { apiRequest } from '../services/apiClient';
import {
  notifyDirectorDataUpdated,
  notifyDirectorLocationsUpdated
} from '../utils/directorEvents';
import './LocationSetupPage.css';

const LOCATION_SETUP_STORAGE_KEY = 'manager-ops-location-setup-id';
const CURRENT_YEAR = new Date().getUTCFullYear();
const CURRENT_MONTH = new Date().getUTCMonth() + 1;

function getLocationSetupId() {
  try {
    return window.sessionStorage.getItem(LOCATION_SETUP_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function getCompletedSteps(status) {
  return new Set(status?.progress?.completedSteps || status?.location?.onboarding?.completedSteps || []);
}

function LocationSetupPage({ onNavigate }) {
  const [locationId, setLocationId] = useState(getLocationSetupId);
  const [locations, setLocations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [users, setUsers] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [status, setStatus] = useState(null);
  const [selectedLeaderId, setSelectedLeaderId] = useState('');
  const [selectedKpiId, setSelectedKpiId] = useState('');
  const [copySourceLocationId, setCopySourceLocationId] = useState('');
  const [isCopyConfirmed, setIsCopyConfirmed] = useState(false);
  const [goalValue, setGoalValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const location = status?.location || locations.find(item => String(item.id) === String(locationId)) || null;
  const completedSteps = useMemo(() => getCompletedSteps(status), [status]);
  const setupMode = status?.progress?.setupMode || location?.onboarding?.setupMode || '';
  const goalCount = Number(status?.location?.onboarding?.goalCount || location?.onboarding?.goalCount || 0);

  const refresh = useCallback(async (nextLocationId = locationId) => {
    setIsLoading(true);
    setError('');
    try {
      const [locationRows, managerRows, userRows, kpiRows] = await Promise.all([
        apiRequest('/api/locations'),
        apiRequest('/api/managers?includeInactive=true'),
        apiRequest('/api/users'),
        apiRequest('/api/kpis')
      ]);
      const setupStatus = nextLocationId
        ? await apiRequest(`/api/location-onboarding/${nextLocationId}`)
        : null;
      setLocations(locationRows);
      setManagers(managerRows);
      setUsers(userRows);
      setKpis(kpiRows);
      setStatus(setupStatus);
      setSelectedLeaderId(setupStatus?.location?.primaryDirectorOwnerId ? String(setupStatus.location.primaryDirectorOwnerId) : '');
    } catch (requestError) {
      setError(requestError.message || 'Unable to load location setup.');
    } finally {
      setIsLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const userByManagerId = useMemo(
    () => new Map(users.filter(user => user.managerId).map(user => [Number(user.managerId), user])),
    [users]
  );

  const leaderOptions = useMemo(
    () => managers
      .filter(manager => String(manager.managerRoleKey || '').toLowerCase() === 'director')
      .filter(manager => {
        if (!location) {
          return false;
        }
        if (Number(manager.locationId) === Number(location.id)) {
          return true;
        }
        const linkedUser = userByManagerId.get(Number(manager.id));
        if (!linkedUser) {
          return false;
        }
        if (linkedUser.accessAllLocations) {
          return true;
        }
        return (linkedUser.assignedLocationIds || []).some(id => Number(id) === Number(location.id));
      })
      .sort((a, b) => `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`)),
    [location, managers, userByManagerId]
  );

  const copySourceOptions = useMemo(
    () => locations
      .filter(item => item.isActive)
      .filter(item => Number(item.id) !== Number(locationId))
      .sort((a, b) => String(a.code).localeCompare(String(b.code))),
    [locations, locationId]
  );

  const handleLocationChange = (event) => {
    const nextLocationId = event.target.value;
    setLocationId(nextLocationId);
    setCopySourceLocationId('');
    setIsCopyConfirmed(false);
    try {
      window.sessionStorage.setItem(LOCATION_SETUP_STORAGE_KEY, nextLocationId);
    } catch {
      // Keep setup usable when storage is unavailable.
    }
    refresh(nextLocationId).catch(() => {});
  };

  const saveProgress = async (currentStep, nextCompletedSteps) => {
    const nextStatus = await apiRequest(`/api/location-onboarding/${locationId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify({
        currentStep,
        completedSteps: Array.from(nextCompletedSteps)
      })
    });
    setStatus(nextStatus);
    return nextStatus;
  };

  const assignLeader = async () => {
    if (!selectedLeaderId) {
      setError('Choose a location leader before saving.');
      return;
    }

    setIsSaving(true);
    setError('');
    setMessage('');
    try {
      const updatedLocation = await apiRequest(`/api/locations/${locationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ primaryDirectorOwnerId: Number(selectedLeaderId) })
      });
      const nextSteps = new Set(completedSteps);
      nextSteps.add('leader');
      await saveProgress('setup_mode', nextSteps);
      setLocations(current => current.map(item => (Number(item.id) === Number(updatedLocation.id) ? updatedLocation : item)));
      setMessage('Location leader saved.');
      notifyDirectorLocationsUpdated();
    } catch (requestError) {
      setError(requestError.message || 'Unable to save location leader.');
    } finally {
      setIsSaving(false);
    }
  };

  const chooseSetupMode = async (mode, extraPayload = {}) => {
    setIsSaving(true);
    setError('');
    setMessage('');
    try {
      const nextStatus = await apiRequest(`/api/location-onboarding/${locationId}/setup-mode`, {
        method: 'POST',
        body: JSON.stringify({ setupMode: mode, ...extraPayload })
      });
      setStatus(nextStatus);
      if (mode === 'company_kpi_template') {
        setMessage(`Company KPI template applied${nextStatus.createdGoalCount ? ` with ${nextStatus.createdGoalCount} draft goals` : ''}.`);
      } else if (mode === 'copy_existing_location') {
        setMessage(`Existing location setup copied with ${nextStatus.createdGoalCount || 0} draft goals.`);
      } else {
        setMessage('Blank setup mode selected.');
      }
      notifyDirectorLocationsUpdated();
    } catch (requestError) {
      setError(requestError.message || 'Unable to save setup mode.');
    } finally {
      setIsSaving(false);
    }
  };

  const copyExistingLocation = () => chooseSetupMode('copy_existing_location', {
    sourceLocationId: Number(copySourceLocationId),
    confirmedCopy: isCopyConfirmed
  });

  const createFirstGoal = async () => {
    if (!selectedKpiId || !goalValue) {
      setError('Choose a KPI and enter a first goal value.');
      return;
    }

    setIsSaving(true);
    setError('');
    setMessage('');
    try {
      await apiRequest('/api/goals/location', {
        method: 'POST',
        body: JSON.stringify({
          locationId: Number(locationId),
          kpiDefinitionId: Number(selectedKpiId),
          goalPeriodType: 'monthly',
          goalYear: CURRENT_YEAR,
          goalMonth: CURRENT_MONTH,
          goalValue: Number(goalValue),
          goalStatus: 'draft',
          isLocked: false,
          statusNote: 'Created during location starter setup.'
        })
      });
      const nextSteps = new Set(completedSteps);
      nextSteps.add('goals');
      await saveProgress('team', nextSteps);
      setGoalValue('');
      setSelectedKpiId('');
      await refresh(locationId);
      setMessage('First goal saved.');
      notifyDirectorLocationsUpdated();
      notifyDirectorDataUpdated('Location setup goals updated.');
    } catch (requestError) {
      setError(requestError.message || 'Unable to save first goal.');
    } finally {
      setIsSaving(false);
    }
  };

  const acknowledgeTeam = async () => {
    setIsSaving(true);
    setError('');
    setMessage('');
    try {
      const nextSteps = new Set(completedSteps);
      nextSteps.add('team');
      await saveProgress('activate', nextSteps);
      setMessage('Team setup acknowledged.');
    } catch (requestError) {
      setError(requestError.message || 'Unable to update team setup.');
    } finally {
      setIsSaving(false);
    }
  };

  const activateLocation = async () => {
    setIsSaving(true);
    setError('');
    setMessage('');
    try {
      const nextStatus = await apiRequest(`/api/location-onboarding/${locationId}/activate`, { method: 'POST' });
      setStatus(nextStatus);
      setMessage(`${nextStatus.location.code} is active and available in dashboards.`);
      notifyDirectorLocationsUpdated();
      notifyDirectorDataUpdated(`${nextStatus.location.code} dashboard activated.`);
    } catch (requestError) {
      setError(requestError.message || 'Unable to activate this location.');
    } finally {
      setIsSaving(false);
    }
  };

  // Sprint 3B.7: LocationSetupPage migrated to WorkspacePage with
  // width="operational". The inline .location-setup-header block is
  // removed — its eyebrow / title / subtitle / back button flow into
  // WorkspacePage's standard slots. The inner .location-setup-page
  // wrapper survives so the existing flex/gap layout (the body's
  // dynamic article sections) keeps applying.
  const headerActions = (
    <>
      <button type="button" onClick={() => onNavigate('/locations')}>
        <i className="fas fa-arrow-left"></i>
        Locations
      </button>
      <button type="button" onClick={() => onNavigate('/setup')}>
        <i className="fas fa-list-check"></i>
        Return to Setup Wizard
      </button>
    </>
  );

  return (
    <WorkspacePage
      data-workspace-id="location-setup"
      width="operational"
      eyebrow="Location Starter Mode"
      title={location ? `${location.code} - ${location.name}` : 'Location Setup'}
      subtitle="This location is ready to activate as soon as its basic information is saved. Leaders, KPI template, and goals can be added any time — before or after activation."
      actions={headerActions}
    >
      <section className="location-setup-page">
      <div className="location-setup-select">
        <label htmlFor="location-setup-select">Setup Location</label>
        <select id="location-setup-select" value={locationId} onChange={handleLocationChange}>
          <option value="">Choose a location</option>
          {locations
            .filter(item => !item.isActive || item.onboarding)
            .map(item => (
              <option key={item.id} value={item.id}>{item.code} - {item.name}</option>
            ))}
        </select>
        {status ? <span className="location-setup-status">{status.status}</span> : null}
      </div>

      {isLoading ? <div className="location-setup-message">Loading setup...</div> : null}
      {message ? <div className="location-setup-message success">{message}</div> : null}
      {error ? <div className="location-setup-message error">{error}</div> : null}

      {location ? (
        <div className="location-setup-grid">
          <article className="location-setup-card launch">
            <span className={location.isActive ? 'step-state done' : 'step-state'}>1</span>
            <h3>Activate Dashboard</h3>
            <p>Activation makes this location visible in dashboards, scorecards, executive views, and normal rollups.</p>
            {location.isActive ? (
              <p className="location-setup-optional-badge">Active — leaders, KPI template, and goals can be added any time below.</p>
            ) : (
              <p className="location-setup-optional-badge">
                Ready now — this location&apos;s basic information (code and name) is all that&apos;s required.
                Leaders, setup mode, first goals, and team acknowledgement are optional and can be completed
                before or after activation.
              </p>
            )}
            <button type="button" onClick={activateLocation} disabled={isSaving || location.isActive}>
              {location.isActive ? 'Active' : 'Activate Dashboard'}
            </button>
          </article>

          <article className="location-setup-card">
            <span className={completedSteps.has('leader') || location.primaryDirectorOwnerId ? 'step-state done' : 'step-state optional'}>2</span>
            <h3>Assign Location Manager</h3>
            <p className="location-setup-optional-badge">Optional — assign after adding leaders.</p>
            <p>
              {leaderOptions.length
                ? 'Choose a leader who is already scoped to this company and location.'
                : 'No eligible Directors are scoped to this location yet. Add a leader from Team first, then come back to designate an owner.'}
            </p>
            <select aria-label="Assign Location Manager" value={selectedLeaderId} onChange={(event) => setSelectedLeaderId(event.target.value)} disabled={!leaderOptions.length}>
              <option value="">Choose leader</option>
              {leaderOptions.map(manager => (
                <option key={manager.id} value={manager.id}>
                  {manager.firstName} {manager.lastName} ({manager.roleTitle})
                </option>
              ))}
            </select>
            <div className="location-setup-actions">
              <button type="button" onClick={assignLeader} disabled={isSaving || !selectedLeaderId}>Save Leader</button>
              <button type="button" onClick={() => onNavigate('/managers')}>Add a Leader</button>
            </div>
          </article>

          <article className="location-setup-card">
            <span className={setupMode ? 'step-state done' : 'step-state optional'}>3</span>
            <h3>Choose Setup Mode</h3>
            <p className="location-setup-optional-badge">Optional — a later step, not required to activate.</p>
            <p>Start with no copied records, or create draft goals from this company’s KPI template.</p>
            <div className="location-setup-actions">
              <button type="button" className={setupMode === 'start_blank' ? 'selected' : ''} onClick={() => chooseSetupMode('start_blank')} disabled={isSaving}>
                Start Blank
              </button>
              <button type="button" className={setupMode === 'company_kpi_template' ? 'selected' : ''} onClick={() => chooseSetupMode('company_kpi_template')} disabled={isSaving}>
                Use Company KPI Template
              </button>
            </div>
            <div className="location-copy-box">
              <label htmlFor="copy-source-location">Copy Existing Location</label>
              <select
                id="copy-source-location"
                value={copySourceLocationId}
                onChange={(event) => {
                  setCopySourceLocationId(event.target.value);
                  setIsCopyConfirmed(false);
                }}
                disabled={isSaving || goalCount > 0}
              >
                <option value="">Choose active source location</option>
                {copySourceOptions.map(item => (
                  <option key={item.id} value={item.id}>{item.code} - {item.name}</option>
                ))}
              </select>
              <label className="location-copy-confirm">
                <input
                  type="checkbox"
                  checked={isCopyConfirmed}
                  onChange={(event) => setIsCopyConfirmed(event.target.checked)}
                  disabled={isSaving || !copySourceLocationId || goalCount > 0}
                />
                Copy only current goal setup as drafts; leave team, actuals, approvals, allocations, events, and history untouched.
              </label>
              <button
                type="button"
                className={setupMode === 'copy_existing_location' ? 'selected' : ''}
                onClick={copyExistingLocation}
                disabled={isSaving || !copySourceLocationId || !isCopyConfirmed || goalCount > 0}
              >
                Copy Setup
              </button>
              {goalCount > 0 ? <p>Copy is available before first goals are added.</p> : null}
            </div>
          </article>

          <article className="location-setup-card">
            <span className={goalCount > 0 ? 'step-state done' : 'step-state optional'}>4</span>
            <h3>Set First Goals</h3>
            <p className="location-setup-optional-badge">Optional — a later step, not required to activate.</p>
            <p>{goalCount > 0 ? `${goalCount} draft goal${goalCount === 1 ? '' : 's'} ready.` : 'Add first goals whenever you’re ready.'}</p>
            <select aria-label="Choose KPI" value={selectedKpiId} onChange={(event) => setSelectedKpiId(event.target.value)}>
              <option value="">Choose KPI</option>
              {kpis.map(kpi => (
                <option key={kpi.id} value={kpi.id}>{kpi.label}</option>
              ))}
            </select>
            <input
              type="number"
              aria-label="Monthly goal value"
              value={goalValue}
              onChange={(event) => setGoalValue(event.target.value)}
              placeholder="Monthly goal value"
            />
            <button type="button" onClick={createFirstGoal} disabled={isSaving || !selectedKpiId || !goalValue}>Save First Goal</button>
          </article>

          <article className="location-setup-card">
            <span className={completedSteps.has('team') ? 'step-state done' : 'step-state optional'}>5</span>
            <h3>Invite Team</h3>
            <p className="location-setup-optional-badge">Optional — a later step, not required to activate.</p>
            <p>Add users and managers from the Team area, then acknowledge whenever this location&apos;s team is ready.</p>
            <div className="location-setup-actions">
              <button type="button" onClick={() => onNavigate('/managers')}>Open Team</button>
              <button type="button" onClick={acknowledgeTeam} disabled={isSaving}>Team Ready</button>
            </div>
          </article>
        </div>
      ) : null}
      </section>
    </WorkspacePage>
  );
}

export default LocationSetupPage;
